import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import 'date-fns';
import { Stack, TextField, TablePagination, CircularProgress } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';

import { activityReportService } from '../../services';
import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import {
  sa, DataTable, PillButton, SectionHeaderRow, SelectPill,
} from '../../components/superAdminUi';

const calculateDays = (addedDate) => {
  return Math.floor((new Date() - new Date(addedDate)) / 86400000);
  // 24 * 60 * 60 * 1000 = 86400000
};

// A registration is "stale" when it has sat for a week with no staff added — the row the team
// needs to chase. Preserved from the original red-row highlight, now surfaced as a badge.
const isStaleRegistration = (row) =>
  calculateDays(row.AddedDate) > 6 && (row.SchoolStaffs || []).length < 1;

const formatDate = (value) => (value == null ? null : moment(value).local().format('DD-MM-YY'));

const lastSurvey = (row) => (row.Surveys && row.Surveys.length ? row.Surveys.slice(-1)[0] : null);

const firstSubmissionDate = (row) => {
  let submissionDate = null;
  (row.Surveys || []).forEach((survey) => {
    const submission = survey.SurveySubmissions.slice(-1)[0];
    submissionDate = submission == null ? null : formatDate(submission.SubmissionDate);
  });
  return submissionDate;
};

const dash = (value) => (value == null || value === '' ? '—' : value);

const statusFilterOptions = [
  { label: 'All', value: 'All' },
  { label: 'SchoolDetail', value: 'SchoolDetail' },
  { label: 'InVoiceDetail', value: 'InVoiceDetail' },
  { label: 'Completed', value: 'Completed' },
  { label: 'WellbeeVoice', value: 'WellbeeVoice' },
  { label: 'WelbeeHeadTeacherDetail', value: 'WelbeeHeadTeacherDetail' },
  { label: 'EmailVerification', value: 'EmailVerification' },
];

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 8px 4px 24px;
  font-family: ${sa.font.family};
`;

const Controls = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    font-size: 18px;
    color: ${sa.color.textGray};
    pointer-events: none;
  }
  input {
    width: 220px;
    max-width: 100%;
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    background: ${sa.color.white};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 9px 12px 9px 38px;

    &::placeholder {
      color: ${sa.color.textGray};
    }
    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

const SchoolCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const SchoolName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StaleTag = styled.span`
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: ${sa.radius.sm};
  background: ${sa.color.groupBg};
  color: ${sa.color.pink};
  font-size: 11px;
  font-weight: ${sa.font.bold};
  line-height: 1.4;
`;

const StateRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${sa.color.textGray};
  font-family: ${sa.font.family};
  font-size: 13px;
`;

const PaginationWrap = styled.div`
  .MuiTablePagination-root {
    font-family: ${sa.font.family};
    color: ${sa.color.textGray};
  }
`;

const columns = [
  {
    key: 'school',
    label: 'School',
    width: '16%',
    render: (row) => (
      <SchoolCell>
        <SchoolName>{dash(row.Name)}</SchoolName>
        {isStaleRegistration(row) && <StaleTag>No staff · {calculateDays(row.AddedDate)}d</StaleTag>}
      </SchoolCell>
    ),
  },
  {
    key: 'fullName',
    label: 'Full Name',
    render: (row) => dash(row.HeadTeachers.length ? row.HeadTeachers[0].FullName : null),
  },
  {
    key: 'email',
    label: 'Email',
    width: '14%',
    render: (row) => dash(row.HeadTeachers.length ? row.HeadTeachers[0].User.Email : null),
  },
  { key: 'accountType', label: 'Account Type', render: (row) => dash(row.WelbeeType) },
  { key: 'status', label: 'Status', render: (row) => dash(row.SignUpStatus) },
  { key: 'registrationDate', label: 'Registration Date', render: (row) => dash(formatDate(row.AddedDate)) },
  { key: 'matGroup', label: 'MAT Group', render: (row) => dash(row.MatGroup === null ? null : row.MatGroup.Name) },
  {
    key: 'surveyStart',
    label: 'Survey Start',
    render: (row) => {
      const survey = lastSurvey(row);
      return dash(survey ? formatDate(survey.StartDate) : null);
    },
  },
  {
    key: 'surveyEnd',
    label: 'Survey End',
    render: (row) => {
      const survey = lastSurvey(row);
      return dash(survey ? formatDate(survey.EndDate) : null);
    },
  },
  { key: 'firstSubmission', label: 'First Submission', render: (row) => dash(firstSubmissionDate(row)) },
  {
    key: 'surveyClosed',
    label: 'Survey Closed',
    render: (row) => {
      const survey = lastSurvey(row);
      if (survey == null) return dash(null);
      const result = survey.IsSurveyClosed.toString();
      return result === 'true' ? 'Yes' : result === 'false' ? 'No' : dash(null);
    },
  },
  { key: 'updatedDate', label: 'Updated Date', render: (row) => dash(formatDate(row.UpdatedDate)) },
];

class AccountRegistrations extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      loading: false,
      totalRows: 0,
      rowPerPage: 10,
      currentPage: 1,
      start: 0,
      filterText: null,
      fromDate: new Date(),
      toDate: new Date(),
      testOnly: false,
      archiveOnly: false,
      statusFilterSelectedOption: { label: 'All', value: 'All' },
    };
  }

  async componentDidMount() {
    // Load initial data when component mounts
    this.filterHandler();
  }

  statusChangeHandler = (value) => {
    const statusFilterSelectedOption =
      statusFilterOptions.find((option) => option.value === value) || statusFilterOptions[0];
    this.setState({ statusFilterSelectedOption }, this.filterHandler);
  };

  filterHandler = (filterText) => {
    this.setState({ loading: true, filterText: filterText });
    const { rowPerPage, currentPage } = this.state;

    let skip = 0;
    if (currentPage > 1) {
      skip = rowPerPage * (currentPage - 1);
    }

    const model = {
      Length: rowPerPage,
      Draw: currentPage,
      Start: skip,
      Search: {
        Value: filterText,
      },
      Columns: [
        {
          Name: 'FromDate',
          Search: {
            Value: this.state.fromDate,
          },
        },
        {
          Name: 'ToDate',
          Search: {
            Value: this.state.toDate,
          },
        },
        {
          Name: 'Status',
          Search: {
            Value: this.state.statusFilterSelectedOption.value,
          },
        },
        {
          Name: 'OnlyTestAccounts',
          Search: {
            Value: this.state.testOnly,
          },
        },
        {
          Name: 'OnlyArchives',
          Search: {
            Value: this.state.archiveOnly,
          },
        },
      ],
    };

    activityReportService.getAccountRegistrations(model).then((response) => {
      this.setState({
        data: response.data.Data,
        totalRows: response.data.RecordsFiltered,
        start: response.data.Start,
        loading: false,
      });
    });
  };

  rowPerPageHandler = async (rowPerPage) => {
    this.setState({ rowPerPage, currentPage: 1 }, () => {
      this.filterHandler();
    });
  };

  pageChangeHandler = async (page) => {
    this.setState({ currentPage: page }, () => {
      this.filterHandler();
    });
  };

  ArchivedClicked(e) {
    this.setState(
      {
        archiveOnly: e.target.checked,
      },
      this.filterHandler,
    );
  }

  TestAccountCheckedClicked(e) {
    this.setState(
      {
        testOnly: e.target.checked,
      },
      this.filterHandler,
    );
  }

  render() {
    const { loading, data, totalRows, currentPage, rowPerPage } = this.state;

    const emptyNode = loading ? (
      <StateRow>
        <CircularProgress size={18} sx={{ color: sa.color.teal }} />
        Loading registrations…
      </StateRow>
    ) : (
      'No registrations found.'
    );

    return (
      <AppContainer>
        <SideMenu />
        <BodyContainer>
          <Header />
          <MainContentContainer>
            <PageWrap>
              <SectionHeaderRow title='Account Registrations Report' size='lg'>
                <Controls>
                  <SearchBox>
                    <SearchIcon />
                    <input
                      type='text'
                      id='search'
                      placeholder='Enter keyword'
                      onChange={(e) => this.filterHandler(e.target.value)}
                      aria-label='Search registrations'
                    />
                  </SearchBox>

                  <SelectPill
                    value={this.state.statusFilterSelectedOption.value}
                    onChange={this.statusChangeHandler}
                    options={statusFilterOptions}
                    ariaLabel='Status'
                    testId='status-dropdown'
                  />

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
                      <DatePicker
                        label='From Date'
                        inputFormat='dd-MM-yyyy'
                        value={this.state.fromDate}
                        onChange={(date) => this.setState({ fromDate: date })}
                        renderInput={(params) => <TextField size='small' {...params} />}
                      />
                      <DatePicker
                        label='To Date'
                        inputFormat='dd-MM-yyyy'
                        value={this.state.toDate}
                        onChange={(date) => this.setState({ toDate: date })}
                        renderInput={(params) => <TextField size='small' {...params} />}
                      />
                    </Stack>
                  </LocalizationProvider>

                  <PillButton type='button' $size='lg' onClick={() => this.filterHandler()}>
                    Generate
                  </PillButton>
                </Controls>
              </SectionHeaderRow>

              <div>
                <DataTable
                  columns={columns}
                  rows={loading ? [] : data}
                  getRowKey={(row, index) => row.EncryptedSchoolId || row.SchoolId || row.Name || index}
                  empty={emptyNode}
                />

                <PaginationWrap>
                  <TablePagination
                    component='div'
                    count={totalRows}
                    page={Math.max(0, currentPage - 1)}
                    rowsPerPage={rowPerPage}
                    onPageChange={(_event, next) => this.pageChangeHandler(next + 1)}
                    onRowsPerPageChange={(event) => this.rowPerPageHandler(parseInt(event.target.value, 10))}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </PaginationWrap>
              </div>
            </PageWrap>
          </MainContentContainer>
        </BodyContainer>
      </AppContainer>
    );
  }
}

export default connect()(withRouter(AccountRegistrations));
